import { useEffect } from "react";
import { DEFAULT_OG_IMAGE, SITE_LOCALE, SITE_NAME, SITE_URL } from "../seo/site";

const upsertMeta = (attr, key, content) => {
  if (!content) {
    return;
  }

  let element = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertLink = (rel, href) => {
  if (!href) {
    return;
  }

  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const upsertJsonLd = (payload) => {
  const scripts = [...document.head.querySelectorAll('script[data-seo-jsonld="true"]')];
  scripts.forEach((script) => script.remove());

  const entries = Array.isArray(payload) ? payload.filter(Boolean) : payload ? [payload] : [];
  entries.forEach((entry, index) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-jsonld", "true");
    script.setAttribute("data-seo-jsonld-index", String(index));
    script.text = JSON.stringify(entry);
    document.head.appendChild(script);
  });
};

function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noIndex = false,
  jsonLd
}) {
  useEffect(() => {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const canonical = `${SITE_URL}${normalizedPath === "/" ? "" : normalizedPath}`;
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

    document.title = fullTitle;
    document.documentElement.lang = "en";

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    upsertMeta("name", "googlebot", noIndex ? "noindex, nofollow" : "index, follow");
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta("name", "theme-color", "#1a56db");
    upsertMeta("name", "application-name", SITE_NAME);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", SITE_LOCALE);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    upsertLink("canonical", canonical);

    upsertJsonLd(jsonLd);
  }, [title, description, path, image, type, noIndex, jsonLd]);

  return null;
}

export default Seo;
